import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Auth0ProviderWrapper from './auth/Auth0Provider.tsx';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Home from './components/Home';
import Compass from './components/Compass';
import Analytics from './components/Analytics';
import CollegeExplore from './components/CollegeExplore';
import CollegeProfile from './components/CollegeProfile';
import CollegeAnalytics from './components/CollegeAnalytics';
import ResourcePage from './components/ResourcePage';
import GPACalculator from './components/GPACalculator';
import PathwayPlanner from './components/PathwayPlanner';

import Chatbot from './components/Chatbot';
import ProfilePanel from './components/ProfilePanel';
import AccountSettings from './components/AccountSettings';

import SignOut from './components/SignOut';
import ProtectedRoute from './components/ProtectedRoute';
import FloatingActionButton from './components/FloatingActionButton';
import RoleSelection from './components/RoleSelection';
import CounselorVerification from './components/CounselorVerification';
import ProfileBuilder from './components/ProfileBuilder/ProfileBuilder';
import CounselorConnect from './components/CounselorConnect/CounselorConnect';
import './utils/dataCleanup'; // Import data cleanup utilities
import './utils/clearActivities'; // Import activity clearing utilities
import './App.css';
import styles from './App.module.css';

// Client-side only wrapper to prevent hydration mismatches
const ClientOnly = ({ children }) => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  if (!hasMounted) {
    return <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '18px',
      color: '#666'
    }}>
      Loading...
    </div>;
  }

  return children;
};

// App content wrapper that can use useLocation
const AppContent = () => {
  const [isProfilePanelOpen, setProfilePanelOpen] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false); // Always start closed
  const [userProfile, setUserProfile] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const location = useLocation();

  // Track screen size for responsive behavior
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1025);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Simplified sidebar state management
  const handleSidebarToggle = () => {
    console.log('Sidebar toggle clicked, current state:', isSidebarOpen);
    setSidebarOpen(prev => !prev);
  };

  const handleSidebarClose = () => {
    console.log('Sidebar close clicked');
    setSidebarOpen(false);
  };

  // Close sidebar when route changes on mobile
  useEffect(() => {
    const handleRouteChange = () => {
      if (!isDesktop) {
        setSidebarOpen(false);
      }
    };

    handleRouteChange();
  }, [location.pathname, isDesktop]);

  useEffect(() => {
    // Only access localStorage on the client side
    if (typeof window !== 'undefined') {
      // Restore data from backup if main storage is empty
      const restoreFromBackup = () => {
        try {
          const backup = localStorage.getItem('quila_data_backup');
          if (backup) {
            const backupData = JSON.parse(backup);
            
            // Restore college list if missing
            if (!localStorage.getItem('myCollegeList') && backupData.collegeList) {
              localStorage.setItem('myCollegeList', JSON.stringify(backupData.collegeList));
              console.log('[App] Restored college list from backup');
            }
            
            // Restore activities if missing
            if (!localStorage.getItem('collegeActivities') && backupData.activities) {
              localStorage.setItem('collegeActivities', JSON.stringify(backupData.activities));
              console.log('[App] Restored activities from backup');
            }
            
            // Restore academic profile if missing
            if (!localStorage.getItem('academicProfile') && backupData.academicProfile) {
              localStorage.setItem('academicProfile', JSON.stringify(backupData.academicProfile));
              console.log('[App] Restored academic profile from backup');
            }
          }
        } catch (error) {
          console.error('[App] Error restoring from backup:', error);
        }
      };
      
      restoreFromBackup();
      
      const savedProfile = localStorage.getItem('userProfile');
      if (savedProfile) {
        try {
          setUserProfile(JSON.parse(savedProfile));
        } catch (error) {
          console.error('Error parsing saved profile:', error);
          localStorage.removeItem('userProfile');
        }
      }
      
      // Initialize empty activities state for new users
      const existingActivities = localStorage.getItem('collegeActivities');
      if (!existingActivities || existingActivities === 'null' || existingActivities === 'undefined') {
        localStorage.setItem('collegeActivities', JSON.stringify([]));
        console.log('[App] Initialized empty activities state for new user');
      }
    }
  }, []);

  // Listen for profile updates from AccountSettings
  useEffect(() => {
    const handleProfileUpdate = () => {
      const savedProfile = localStorage.getItem('userProfile');
      if (savedProfile) {
        try {
          setUserProfile(JSON.parse(savedProfile));
        } catch (error) {
          console.error('Error parsing updated profile:', error);
        }
      }
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  const handleSaveProfile = (profile) => {
    setUserProfile(profile);
    if (typeof window !== 'undefined') {
      localStorage.setItem('userProfile', JSON.stringify(profile));
      // Also save to academicProfile for consistency with other components
      localStorage.setItem('academicProfile', JSON.stringify(profile));
      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent(new Event('academicProfileUpdated'));
      // Also dispatch userProfileUpdated event
      window.dispatchEvent(new Event('userProfileUpdated'));
    }
    // Don't automatically close the panel - let user close it manually
    // setProfilePanelOpen(false);
  };

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  return (
    <div className={styles.appContainer}>
      {/* Debug panel - remove in production */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '10px',
          borderRadius: '5px',
          fontSize: '12px',
          zIndex: 9999,
          fontFamily: 'monospace'
        }}>
          <div>Sidebar: {isSidebarOpen ? 'OPEN' : 'CLOSED'}</div>
          <div>Desktop: {isDesktop ? 'YES' : 'NO'}</div>
          <div>Screen: {window.innerWidth}px</div>
        </div>
      )}
      
      <Sidebar 
        isOpen={isSidebarOpen}
        onClose={handleSidebarClose}
        onToggle={handleSidebarToggle}
      />
      
      <div className={`${styles.mainContent} ${isSidebarOpen && isDesktop ? styles.mainContentWithSidebar : ''}`}>
        <Routes>
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <Home />
                </>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/role-selection" 
            element={
              <ProtectedRoute>
                <RoleSelection />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/counselor-verification" 
            element={
              <ProtectedRoute>
                <CounselorVerification />
              </ProtectedRoute>
            } 
          />

          <Route path="/signout" element={<SignOut />} />
          
          <Route 
            path="/compass" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <Compass />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/analytics" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <Analytics />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/explore" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <CollegeExplore />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/college-profile/:collegeName" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <CollegeProfile />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/college-analytics/:collegeName" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <CollegeAnalytics />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/resource-page" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <ResourcePage />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/gpa-calculator" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <GPACalculator />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/pathway-planner" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <PathwayPlanner />
                </div>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/chatbot" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <Chatbot />
                </div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <AccountSettings />
                </div>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/profile-builder" 
            element={
              <ProtectedRoute>
                <div className={styles.pageContainer}>
                  <Header 
                    onMenuClick={handleSidebarToggle}
                    onProfileClick={() => setProfilePanelOpen(true)}
                  />
                  <ProfileBuilder />
                </div>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/counselor-connect" 
            element={
              <ProtectedRoute>
                <CounselorConnect />
              </ProtectedRoute>
            } 
          />

        </Routes>

        {/* Only show FloatingActionButton when not on role selection, counselor verification, profile builder, or counselor connect pages */}
        {location.pathname !== '/role-selection' && location.pathname !== '/counselor-verification' && location.pathname !== '/profile-builder' && location.pathname !== '/counselor-connect' && (
          <FloatingActionButton
            onClick={() => setProfilePanelOpen(true)}
            isProfilePanelOpen={isProfilePanelOpen}
          />
        )}
        <ProfilePanel
          isOpen={isProfilePanelOpen}
          onClose={() => setProfilePanelOpen(false)}
          onSave={handleSaveProfile}
          initialProfile={userProfile}
        />
        {/* Only render footer on home page */}
        {isHomePage && <Footer />}
      </div>
    </div>
  );
};

function App() {
  return (
    <ClientOnly>
      <Auth0ProviderWrapper>
        <AppContent />
      </Auth0ProviderWrapper>
    </ClientOnly>
  );
}

export default App;